import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Text,
    Badge,
    Button,
    IconButton,
    HStack,
    VStack,
    Flex,
    useColorModeValue,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Select,
    useDisclosure,
    useToast,
    InputGroup,
    InputLeftElement,
    Input,
    Card,
    CardBody,
    Stat,
    StatLabel,
    StatNumber,
    SimpleGrid,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Spinner,
    Center,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Switch,
    Collapse,
    Radio,
    RadioGroup,
    FormErrorMessage,
    Stack
} from '@chakra-ui/react';
import {
    MdSearch,
    MdRefresh,
    MdAdd,
    MdMoreVert,
    MdEdit,
    MdDelete,
    MdExpandMore,
} from 'react-icons/md';
import apiService from '../services/apiService';
import ClientAppMappingModal from '../components/Common/ClientAppMappingModal';

import { useSearchParams } from 'react-router-dom';
import { ToastManager } from '../components/Common/ToastManager';
import usageTracker from '../services/usageTracker';

const ClientMappingsManagement = ({ userProfile, appConfig, authServiceReady }) => {
    // ALL THEME-SPECIFIC COLORS

    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const headingColor = useColorModeValue('gray.800', 'white');
    const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
    const inputBg = useColorModeValue('white', 'gray.700');
    const hoverBg = useColorModeValue('gray.50', 'gray.700');
    const tableHeaderBg = useColorModeValue('gray.50', 'gray.900');
    const statsCardBg = useColorModeValue('white', 'gray.800');
    const buttonHoverBorderColor = useColorModeValue('gray.300', 'gray.600');
    const purpleHoverBg = useColorModeValue('purple.50', 'purple.900');
    const purpleBorderColor = useColorModeValue('purple.200', 'purple.600');
    const purpleTextColor = useColorModeValue('purple.600', 'purple.300');
    const purpleHoverBorderColor = useColorModeValue('purple.300', 'purple.500');
    const blueHoverColor = useColorModeValue('blue.600', 'blue.500');
    const badgeVariant = useColorModeValue('subtle', 'solid');
    const menuShadow = useColorModeValue('lg', 'dark-lg');
    const redHoverBg = useColorModeValue('red.50', 'red.900');
    const paginationHoverBorderColor = useColorModeValue('gray.300', 'gray.600');
    const searchIconColor = useColorModeValue('gray.400', 'gray.500');
    const pageSizeHoverBorderColor = useColorModeValue('gray.300', 'gray.600');

    const modalBg = useColorModeValue('#FFFFFF', '#1A1B23');
    const modalBorderColor = useColorModeValue('#E2E4E9', '#2D3748');
    const modalHeaderGradient = useColorModeValue('linear-gradient(135deg, #1a365d 0%, #2c5282 100%)', 'linear-gradient(135deg, #0f1419 0%, #1a202c 100%)');
    const modalHeaderText = useColorModeValue('#FFFFFF', '#F7FAFC');
    const modalHeaderIconBg = useColorModeValue('rgba(255, 255, 255, 0.2)', 'rgba(255, 255, 255, 0.1)');
    const modalCloseButtonHover = useColorModeValue('rgba(255, 255, 255, 0.2)', 'rgba(255, 255, 255, 0.1)');
    const modalFooterBg = useColorModeValue('#FAFBFC', '#171923');


    // Form Input Colors
    const formLabelColor = useColorModeValue('#2D3748', '#E2E8F0');
    const formInputBg = useColorModeValue('#FFFFFF', '#2D3748');
    const formInputBorderColor = useColorModeValue('#D1D5DB', '#4A5568');
    const formInputTextColor = useColorModeValue('#1A202C', '#F7FAFC');
    const formInputHoverBorderColor = useColorModeValue('#3182CE', '#63B3ED');
    const formInputFocusBorderColor = useColorModeValue('#2B6CB0', '#4299E1');
    const formInputFocusShadowColor = useColorModeValue('rgba(66, 153, 225, 0.2)', 'rgba(66, 153, 225, 0.3)');

    // Loading States
    const loadingTextColor = useColorModeValue('#718096', '#A0AEC0');


    // Alert Colors (Info)
    const alertInfoBg = useColorModeValue('#EBF4FF', '#1E2833');
    const alertInfoBorderColor = useColorModeValue('#3182CE', '#2B6CB0');
    const alertInfoIconColor = useColorModeValue('#3182CE', '#63B3ED');
    const alertInfoTitleColor = useColorModeValue('#1E3A8A', '#BEE3F8');
    const alertInfoDescColor = useColorModeValue('#1E40AF', '#90CDF4');

    // Application Preview Colors
    const appPreviewBg = useColorModeValue('linear-gradient(135deg, #EBF4FF 0%, #F0F4F8 100%)', 'linear-gradient(135deg, #1A202C 0%, #2D3748 100%)');
    const appPreviewBorderColor = useColorModeValue('#3182CE', '#4299E1');
    const appPreviewAccentColor = useColorModeValue('#3182CE', '#4299E1');
    const appPreviewTitleColor = useColorModeValue('#1E3A8A', '#BEE3F8');
    const appPreviewSubtitleColor = useColorModeValue('#4A5568', '#A0AEC0');
    const appPreviewDescColor = useColorModeValue('#4A5568', '#CBD5E0');

    // Badge Colors
    const badgePurpleBg = useColorModeValue('#553C9A', '#7C3AED');
    const badgeGreenBg = useColorModeValue('#047857', '#10B981');
    const badgeRedBg = useColorModeValue('#DC2626', '#EF4444');
    const badgeTextColor = useColorModeValue('#FFFFFF', '#FFFFFF');

    // Summary Section Colors
    const summarySectionBg = useColorModeValue('#F9FAFB', '#2D3748');
    const summarySectionBorderColor = useColorModeValue('#E5E7EB', '#4A5568');
    const summaryTextColor = useColorModeValue('#374151', '#E2E8F0');
    const [versionFilter, setVersionFilter] = useState('All Versions');
    const [uniqueVersions, setUniqueVersions] = useState([]);

    // Version Guidelines Colors
    const versionGuidelinesBg = useColorModeValue('#F8FAFC', '#2D3748');
    const versionGuidelinesBorderColor = useColorModeValue('#E2E8F0', '#4A5568');
    const versionGuidelinesTitleColor = useColorModeValue('#374151', '#E2E8F0');
    const versionGuidelinesTextColor = useColorModeValue('#6B7280', '#A0AEC0');

    // Version Preview Colors
    const versionPreviewBg = useColorModeValue('#F0FDF4', '#1F2937');
    const versionPreviewBorderColor = useColorModeValue('#10B981', '#059669');
    const versionPreviewTextColor = useColorModeValue('#047857', '#34D399');
    const versionBadgeBg = useColorModeValue('#059669', '#10B981');
    const versionBadgeTextColor = useColorModeValue('#FFFFFF', '#FFFFFF');

    // Button Colors
    const primaryButtonBg = useColorModeValue('#2563EB', '#3B82F6');
    const primaryButtonTextColor = useColorModeValue('#FFFFFF', '#FFFFFF');
    const primaryButtonHoverBg = useColorModeValue('#1D4ED8', '#2563EB');
    const primaryButtonDisabledBg = useColorModeValue('#D1D5DB', '#4A5568');
    const primaryButtonDisabledTextColor = useColorModeValue('#9CA3AF', '#A0AEC0');

    const cancelButtonTextColor = useColorModeValue('#6B7280', '#A0AEC0');
    const cancelButtonHoverBg = useColorModeValue('#F9FAFB', '#2D3748');

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [searchParams] = useSearchParams();
    const {
        isOpen: isEditOpen,
        onOpen: onEditOpen,
        onClose: onEditClose
    } = useDisclosure();

    // State management
    const [mappings, setMappings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false); // New loading state for filter operations
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [clientFilter, setClientFilter] = useState('All Clients');
    const [pageSize, setPageSize] = useState('20 per page');
    const [currentPage, setCurrentPage] = useState(1);

    // Create/Edit Mapping State
    const [clients, setClients] = useState([]);
    const [availableApplications, setAvailableApplications] = useState([]);
    const [mappingData, setMappingData] = useState({
        selectedClientId: '',
        selectedApplications: [],
        selectedApplication: '',
        mappingVersion: ''
    });
    const [editingMapping, setEditingMapping] = useState(null);
    const [mappingLoading, setMappingLoading] = useState(false);
    const [mappingMode, setMappingMode] = useState('single');



    const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);


    const [applicationFilter, setApplicationFilter] = useState('All Applications');
    const [applications, setApplications] = useState([]);
    const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

    const [mappingSearchTerm, setmappingSearchTerm] = useState('');

    const [editVersionInputMethod, setEditVersionInputMethod] = useState('select');
    const [editVersionValidationError, setEditVersionValidationError] = useState('');

    const isAdminuser = () => {
        return userProfile && 
               (userProfile.role === 'admin' || userProfile.role === 'devops');
    };


    // Fetch client mappings data with optional client filter
    const fetchMappings = async (clientId = null, applicationId = null, showLoading = true) => {
        try {
            if (showLoading) {
                setLoading(true);
            } else {
                setFilterLoading(true);
            }
            setError(null);

            // Prepare API parameters
            const apiParams = {};
            if (clientId) {
                apiParams.clientId = clientId;
            }
            if (applicationId) {
                apiParams.applicationId = applicationId
            }
            // Call API with filter parameters
            const response = await apiService.fetchClientApplicationMappings(apiParams);

            if (response.success && response.data) {
                setMappings(Array.isArray(response.data) ? response.data : []);
            } else {
                throw new Error(response.error || response.message || 'Failed to fetch mappings');
            }
        } catch (error) {
            setError(error.message || 'Failed to load client mappings. Please try again.');
            setMappings([]);
        } finally {
            if (showLoading) {
                setLoading(false);
            } else {
                setFilterLoading(false);
            }
        }
    };

    const getStatusBadge = (status) => {
        return status
            ? { colorScheme: 'green', label: 'ACTIVE' }
            : { colorScheme: 'red', label: 'INACTIVE' };
    };

    // Fetch clients for mapping
    const fetchClients = async () => {
        try {
            setMappingLoading(true);
            const response = await apiService.getClientsList({ size: 1000 });

            if (response.success && response.data && response.data.content) {
                const clientsList = Array.isArray(response.data.content) ? response.data.content : [];
                const activeClients = clientsList.filter(client => !client.deleted);
                setClients(activeClients);
            } else {
                throw new Error(response.error || response.message || 'Failed to fetch clients');
            }
        } catch (error) {
            ToastManager.showError('Failed to load clients. Please try again.')
            setClients([]);
        } finally {
            setMappingLoading(false);
        }
    };


    // Fetch available applications for mapping to selected client
    const fetchAvailableApplicationsForClient = async (clientId) => {
        try {
            setMappingLoading(true);
            const response = await apiService.fetchAvailableApplicationsForClient(clientId);

            if (response.success && response.data) {
                setAvailableApplications(Array.isArray(response.data) ? response.data : []);
            } else {
                throw new Error(response.error || response.message || 'Failed to fetch available applications');
            }
        } catch (error) {
            ToastManager.showError('Failed to load available applications. Please try again.')
            setAvailableApplications([]);
        } finally {
            setMappingLoading(false);
        }
    };

    useEffect(() => {
        const versions = [...new Set(mappings.map(mapping =>
            mapping.mappingVersion || mapping.appVersion
        ))].filter(Boolean).sort();
        setUniqueVersions(versions);
    }, [mappings]);
    // Load mappings on component mount
    useEffect(() => {
        if (!urlParamsProcessed) return
        // fetchMappings();
        fetchClients();
        fetchApplications();
    }, [urlParamsProcessed]);

    useEffect(() => {
        const selectedClient = clientFilter !== 'All Clients' ?
            clients.find(client => client.clientName === clientFilter) : null;
        const clientId = selectedClient ? selectedClient.clientId : null;

        const selectedApp = applicationFilter !== 'All Applications' ?
            applications.find(app => app.appName === applicationFilter) : null;
        const applicationId = selectedApp ? selectedApp.appId : null;
        if (clients.length > 0 && applications.length > 0) {
            fetchMappings(clientId, applicationId, false);
        }
        //fetchMappings(clientId, applicationId, false);
        setCurrentPage(1);
    }, [clientFilter, applicationFilter, clients, applications]);


    useEffect(() => {
        if (urlParamsProcessed) return;

        const filterByApplication = searchParams.get('filterByApplication');

        if (filterByApplication) {
            setApplicationFilter(filterByApplication);
        }

        setUrlParamsProcessed(true);
    }, [urlParamsProcessed, searchParams]);


    const fetchApplications = async () => {
        try {

            const response = await apiService.fetchClientApplications();

            if (response.success && response.data) {
                setApplications(Array.isArray(response.data) ? response.data : []);
            } else {
                throw new Error(response.error || response.message || 'Failed to fetch applications');
            }
        } catch (error) {
            setError(error.message || 'Failed to load applications. Please try again.');
            ToastManager.showError(error.message || 'Failed to load applications. Please try again.')
            setApplications([]);
        } finally {
        }
    };

    // Calculate stats
    const stats = {
        totalMappings: mappings.length,
        uniqueClients: new Set(mappings.map(m => m.client_id)).size,
        uniqueApplications: new Set(mappings.map(m => m.app_id)).size
    };

    const handleSearch = (value) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setClientFilter('All Clients');
        setSearchTerm('');
        setApplicationFilter('All Applications');
        setCurrentPage(1);
        setVersionFilter('All Versions');
    };

    const handleRefresh = () => {
        const currentClientId = clientFilter !== 'All Clients' ?
            (clients.find(client => client.clientName === clientFilter)?.clientId || clientFilter) :
            null;
        const currentAppId = applicationFilter !== 'All Applications' ?
            (applications.find(app => app.appName === applicationFilter)?.appId) :
            null;
        fetchMappings(currentClientId, currentAppId);
    };

    // Handle client filter change
    const handleClientFilterChange = (value) => {
        setClientFilter(value);
    };

    const handleApplicationFilterChange = (value) => {
        setApplicationFilter(value);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    const handleCreateNew = async () => {
        setMappingData({
            selectedClientId: '',
            selectedApplications: [],
            selectedApplication: '',
            mappingVersion: ''
        });
        setEditingMapping(null);
        setAvailableApplications([]);
        setMappingMode('single');
        await fetchClients();
        onOpen();
    };

    const handleModalClose = () => {
        setMappingData({
            selectedClientId: '',
            selectedApplications: [],
            selectedApplication: '',
            mappingVersion: ''
        });
        setAvailableApplications([]);
        setEditingMapping(null);
        setMappingMode('single');
        onClose();
    };

    const handleEditModalClose = () => {
        setEditingMapping(null);
        setEditVersionInputMethod('select');
        setEditVersionValidationError('');
        onEditClose();
    };

    const handleClientChange = async (clientId) => {
        setMappingData(prev => ({
            ...prev,
            selectedClientId: clientId,
            selectedApplications: [],
            selectedApplication: '',
            mappingVersion: ''
        }));

        if (clientId) {
            await fetchAvailableApplicationsForClient(clientId);
        } else {
            setAvailableApplications([]);
        }
    };

    const handleApplicationSelection = (appId) => {
        setMappingData(prev => ({
            ...prev,
            selectedApplication: appId,
            mappingVersion: ''
        }));
    };

    const handleCreateMapping = async () => {
        if (!mappingData.selectedClientId) {
            ToastManager.showError('Please select a client')
            return;
        }

        if (!mappingData.selectedApplication) {
            ToastManager.showError('Please select at least one application to map.')
            return;
        }
        if (!mappingData.mappingVersion) {
            ToastManager.showError('Please Enter mapping Version.')
            return;
        }

        try {
            setMappingLoading(true);
            const selectedApplications = [mappingData.selectedApplication]
            const mappingPayload = {
                clientId: mappingData.selectedClientId,
                applicationIds: selectedApplications,
                createdBy: userProfile.email,
                isEnabled: true,
                mappingVersion: mappingData.mappingVersion
            };
            const response = await apiService.createClientApplicationMapping(mappingPayload);

            if (response.success) {
                usageTracker.action('Client App Mappings', 'MAP');
                ToastManager.showSuccess(response.message || `Successfully mapped ${mappingData.selectedApplications.length} application(s) to client.`)

                handleModalClose();

                // Refresh with current filter
                const currentClientId = clientFilter !== 'All Clients' ?
                    (clients.find(client => client.clientName === clientFilter)?.clientId || clientFilter) :
                    null;
                const currentAppId = applicationFilter !== 'All Applications' ?
                    (applications.find(app => app.appName === applicationFilter)?.appId) :
                    null;
                fetchMappings(currentClientId, currentAppId);
            } else {
                ToastManager.showError(response.error || response.message || 'Failed to create mapping')
                throw new Error(response.error || response.message || 'Failed to create mapping');
            }
        } catch (error) {
            ToastManager.showError(error.message || 'Failed to create application mapping. Please try again.')
        } finally {
            setMappingLoading(false);
        }
    };

    const handleEdit = async (mapping) => {
        try {
            setEditingMapping(mapping);
            onEditOpen();
            setEditVersionInputMethod('manual'); // Set manual as default
            setEditVersionValidationError(''); 
            setIsDetailsExpanded(false)
        } catch (error) {
            ToastManager.showError('Failed to load mapping details. Please try again.');
        }
    };

    const handleUpdateMapping = async () => {
        try {
            if (editVersionValidationError) {
                ToastManager.showError('Please fix version validation errors before updating.');
                return;
            }
            setMappingLoading(true);
            const response = await apiService.updateClientApplicationMapping({
                clientId: editingMapping.clientId,
                applicationId: editingMapping.appId,
                updatedBy: userProfile.email,
                isEnabled: editingMapping.isEnabled,
                mappingVersion: editingMapping.mappingVersion
            });

            if (response.success) {
                usageTracker.action('Client App Mappings', 'UPDATE');
                ToastManager.showSuccess(response.message || 'Mapping has been successfully updated.');
                handleEditModalClose();

                const currentClientId = clientFilter !== 'All Clients' ?
                    (clients.find(client => client.clientName === clientFilter)?.clientId || clientFilter) :
                    null;
                const currentAppId = applicationFilter !== 'All Applications' ?
                    (applications.find(app => app.appName === applicationFilter)?.appId) :
                    null;
                fetchMappings(currentClientId, currentAppId, false);
            } else {
                throw new Error(response.error || response.message || 'Failed to update mapping');
            }
        } catch (error) {
            ToastManager.showError(error.message || 'Failed to update mapping. Please try again.')
        } finally {
            setMappingLoading(false);
        }
    };

    const handleDelete = async (mapping) => {
        try {
            const response = await apiService.deleteClientApplicationMapping({
                clientId: mapping.clientId,
                appId: mapping.appId,
                updatedBy: userProfile.email
            });

            if (response.success) {
                usageTracker.action('Client App Mappings', 'UNMAP');
                ToastManager.showSuccess(`Mapping between ${mapping.client_name} and ${mapping.app_name} has been deleted.`);
                const currentClientId = clientFilter !== 'All Clients' ?
                    (clients.find(client => client.clientName === clientFilter)?.clientId || clientFilter) :
                    null;
                const currentAppId = applicationFilter !== 'All Applications' ?
                    (applications.find(app => app.appName === applicationFilter)?.appId) :
                    null;
                fetchMappings(currentClientId, currentAppId, false);
            } else {
                ToastManager.showError(response.error || response.message || 'Failed to delete mapping')
                throw new Error(response.error || response.message || 'Failed to delete mapping');
            }
        } catch (error) {
            ToastManager.showError(error.message || 'Failed to delete mapping. Please try again.')
        }
    };

    const handleVersionChange = (version) => {
        const cleanVersion = version.trim();
        setMappingData(prev => ({
            ...prev,
            mappingVersion: cleanVersion
        }));
    };

    // Since we're now filtering via API, we only need to filter by search term locally
    const filteredMappings = mappings.filter(mapping => {
        const matchesSearch = searchTerm === '' ||
            (mapping.appName && mapping.appName.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (mapping.clientName && mapping.clientName.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (mapping.appType && mapping.appType.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (mapping.createdBy && mapping.createdBy.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesVersion = versionFilter === 'All Versions' ||
            (mapping.mappingVersion) === versionFilter;

        return matchesSearch && matchesVersion;
    });


    // Filter applications based on search term
    const filteredApplicationsModal = useMemo(() => {
        if (!mappingSearchTerm.trim()) return availableApplications;

        const search = mappingSearchTerm.toLowerCase().trim();
        return availableApplications.filter(app =>
            app.appName.toLowerCase().includes(search) ||
            app.appType.toLowerCase().includes(search) ||
            app.appVersion.toLowerCase().includes(search) ||
            app.appId.toString().includes(search) ||
            (app.appDescription && app.appDescription.toLowerCase().includes(search))
        );
    }, [availableApplications, mappingSearchTerm]);

    const getTypeBadge = (appType) => {
        const typeColorMap = {
            'Web Application': 'blue',
            'WAR': 'orange',
            'Console Application': 'green',
            'Plugins': 'purple',
            'API Service': 'teal',
            'ETL': 'cyan',
            'Others': 'gray'
        };

        return typeColorMap[appType] || 'gray';
    };

    // Pagination calculations
    const itemsPerPage = parseInt(pageSize.split(' ')[0]);
    const totalPages = Math.ceil(filteredMappings.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedMappings = filteredMappings.slice(startIndex, endIndex);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize);
        setCurrentPage(1);
    };

    // Bulk application selection handler
    const handleBulkApplicationSelection = (selectedApps) => {
        setMappingData(prev => ({
            ...prev,
            selectedApplications: selectedApps
        }));
    };

    // Bulk mapping creation
    const handleCreateBulkMapping = async () => {
        if (!mappingData.selectedClientId) {
            ToastManager.showError('Please select a client.')
            return;
        }

        if (!mappingData.selectedApplications?.length) {
            ToastManager.showError('Please select at least one application to map.')
            return;
        }

        try {
            setMappingLoading(true);

            const mappingPayload = {
                clientId: mappingData.selectedClientId,
                applicationIds: mappingData.selectedApplications,
                createdBy: userProfile.email,
                isEnabled: true,
                mappingVersion: null // Default for bulk mapping
            };

            const response = await apiService.createClientApplicationMapping(mappingPayload);

            if (response.success) {
                usageTracker.action('Client App Mappings', 'MAP');
                ToastManager.showSuccess(response.message || `Successfully mapped ${mappingData.selectedApplications.length} applications to client.`)
                handleModalClose();

                // Refresh with current filter
                const currentClientId = clientFilter !== 'All Clients' ?
                    (clients.find(client => client.clientName === clientFilter)?.clientId || clientFilter) :
                    null;
                const currentAppId = applicationFilter !== 'All Applications' ?
                    (applications.find(app => app.appName === applicationFilter)?.appId) :
                    null;
                fetchMappings(currentClientId, currentAppId);
            } else {
                throw new Error(response.error || response.message || 'Failed to create bulk mapping');
            }
        } catch (error) {
            ToastManager.showError(error.message || 'Failed to create bulk application mapping.please try again.')
        } finally {
            setMappingLoading(false);
        }
    };



    // Add these helper functions
    const generateVersionOptions = (appVersion, minimalVersion) => {
        if (!appVersion || !minimalVersion) return [];

        const parseVersion = (version) => {
            const cleaned = version.toString().replace(/^v/, '');
            const parts = cleaned.split('.');
            return {
                major: parseInt(parts[0]) || 0,
                minor: parseInt(parts[1]) || 0,
                patch: parts.length >= 3 ? parseInt(parts[2]) || 0 : null,
                hasThreeParts: parts.length >= 3,
                original: version.toString()
            };
        };

        const formatVersion = (major, minor, patch, hasThreeParts) => {
            if (hasThreeParts && patch !== null) {
                return `${major}.${minor}.${patch}`;
            }
            return `${major}.${minor}`;
        };

        const current = parseVersion(appVersion);
        const minimal = parseVersion(minimalVersion);
        const versionsSet = new Set();

        versionsSet.add(current.original);

        let currentMajor = current.major;
        let currentMinor = current.minor;
        let currentPatch = current.hasThreeParts ? current.patch : null;
        const useThreeParts = current.hasThreeParts || minimal.hasThreeParts;

        while (
            currentMajor > minimal.major ||
            (currentMajor === minimal.major && currentMinor > minimal.minor) ||
            (currentMajor === minimal.major && currentMinor === minimal.minor &&
                useThreeParts && currentPatch !== null && minimal.patch !== null && currentPatch > minimal.patch)
        ) {
            if (useThreeParts && currentPatch !== null && currentPatch > 0) {
                currentPatch--;
            } else if (currentMinor > 0) {
                currentMinor--;
                if (useThreeParts) {
                    currentPatch = 9;
                }
            } else if (currentMajor > 0) {
                currentMajor--;
                currentMinor = 9;
                if (useThreeParts) {
                    currentPatch = 9;
                }
            } else {
                break;
            }

            if (
                currentMajor < minimal.major ||
                (currentMajor === minimal.major && currentMinor < minimal.minor) ||
                (currentMajor === minimal.major && currentMinor === minimal.minor &&
                    useThreeParts && currentPatch !== null && minimal.patch !== null && currentPatch < minimal.patch)
            ) {
                break;
            }

            const versionStr = formatVersion(currentMajor, currentMinor, currentPatch, useThreeParts);
            versionsSet.add(versionStr);
        }

        versionsSet.add(minimal.original);
        return Array.from(versionsSet);
    };


    const compareVersions = (version1, version2) => {
        const parseVersion = (version) => {
            const cleaned = version.toString().replace(/^v/, '');
            const parts = cleaned.split('.');
            return {
                major: parseInt(parts[0]) || 0,
                minor: parseInt(parts[1]) || 0,
                patch: parseInt(parts[2]) || 0
            };
        };

        const v1 = parseVersion(version1);
        const v2 = parseVersion(version2);

        if (v1.major !== v2.major) {
            return v1.major - v2.major;
        }
        if (v1.minor !== v2.minor) {
            return v1.minor - v2.minor;
        }
        return v1.patch - v2.patch;
    };

    const validateManualEditVersion = (inputVersion, appVersion, minimalVersion) => {
        if (!inputVersion || !inputVersion.trim()) {
            return "Version is required";
        }

        const cleanedInput = inputVersion.trim();
        const versionPattern = /^\d+(\.\d+)*$/;
        if (!versionPattern.test(cleanedInput)) {
            return "Version must be in format like '1.0', '2.1.3', etc.";
        }

        if (compareVersions(cleanedInput, minimalVersion) < 0) {
            return `Version must be greater than or equal to minimal version (${minimalVersion})`;
        }

        if (compareVersions(cleanedInput, appVersion) > 0) {
            return `Version must be less than or equal to latest version (${appVersion})`;
        }

        return null;
    };

    const handleEditVersionInputMethodChange = (method) => {
        setEditVersionInputMethod(method);
        setEditVersionValidationError('');
        if (method === 'select' && editingMapping) {
            const versionOptions = generateVersionOptions(editingMapping.appVersion, editingMapping.minimalVersion);
            if (!versionOptions.includes(editingMapping.mappingVersion)) {
                setEditingMapping(prev => ({
                    ...prev,
                    mappingVersion: ''
                }));
            }
        }
    };

    const handleManualEditVersionChange = (inputValue) => {
        setEditingMapping(prev => ({
            ...prev,
            mappingVersion: inputValue
        }));
        if (editingMapping?.appVersion && editingMapping?.minimalVersion) {
            const error = validateManualEditVersion(inputValue, editingMapping.appVersion, editingMapping.minimalVersion);
            setEditVersionValidationError(error || '');
        }
    };
    // Loading state
    if (loading) {
        return (
            <Box>
                <Center py={20}>
                    <VStack spacing={4}>
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                        <Text color={textColor} fontSize="lg">Loading client mappings...</Text>
                    </VStack>
                </Center>
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Box>
                <Alert status="error" bg={cardBg} borderColor="red.300" borderWidth="1px" borderRadius="md">
                    <AlertIcon />
                    <Box flex="1">
                        <AlertTitle color="red.600">Error Loading Client Mappings!</AlertTitle>
                        <AlertDescription color={textColor}>
                            {error}
                        </AlertDescription>
                    </Box>
                    <Button size="sm" colorScheme="red" variant="outline" onClick={handleRefresh}>
                        Retry
                    </Button>
                </Alert>
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Flex justify="space-between" align="flex-start" mb={6}>
                <Box>
                    <Text fontSize="xl" fontWeight="bold" color={headingColor} mb={1}>
                        Client Mappings
                    </Text>
                    <Text color={textColor} fontSize="sm">
                        View and manage application-client mappings
                    </Text>
                </Box>
                <HStack spacing={3}>
                    <Button
                        leftIcon={<MdRefresh />}
                        variant="outline"
                        size="sm"
                        colorScheme="gray"
                        borderColor={borderColor}
                        color={textColor}
                        onClick={handleRefresh}
                        isLoading={filterLoading}
                        _hover={{
                            bg: hoverBg,
                            borderColor: buttonHoverBorderColor
                        }}
                    >
                        Refresh
                    </Button>
                    {isAdminuser() &&(
                        <Button
                            leftIcon={<MdAdd />}
                            colorScheme="blue"
                            size="sm"
                            onClick={handleCreateNew}
                            _hover={{
                                bg: blueHoverColor
                            }}
                        >
                            Create Mapping
                        </Button>
                    )}
                    
                </HStack>
            </Flex>

            {/* Stats Cards */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={6}>
                <Card bg={statsCardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
                    <CardBody>
                        <Stat>
                            <StatLabel color={textColor} fontSize="sm" fontWeight="medium">Total Mappings</StatLabel>
                            <StatNumber fontSize="2xl" fontWeight="bold" color={headingColor}>
                                {filterLoading ? <Spinner size="sm" /> : stats.totalMappings}
                            </StatNumber>
                        </Stat>
                    </CardBody>
                </Card>
            </SimpleGrid>

            {/* Search and Filters */}
            <Card bg={cardBg} boxShadow="sm" mb={6} borderWidth="1px" borderColor={borderColor}>
                <CardBody>
                    <Flex gap={4} mb={4} align="end">
                        <Box flex="1">
                            <InputGroup>
                                <InputLeftElement pointerEvents="none">
                                    <MdSearch color={searchIconColor} />
                                </InputLeftElement>
                                <Input
                                    placeholder="Search by application name, client name, type, or creator"
                                    value={searchTerm}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    bg={inputBg}
                                    borderColor={borderColor}
                                    color={headingColor}
                                    _placeholder={{ color: mutedTextColor }}
                                    _hover={{ borderColor: buttonHoverBorderColor }}
                                    _focus={{
                                        borderColor: 'blue.500',
                                        boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                    }}
                                />
                            </InputGroup>
                        </Box>
                        <Button
                            variant="outline"
                            colorScheme="purple"
                            size="md"
                            onClick={resetFilters}
                            borderColor={purpleBorderColor}
                            color={purpleTextColor}
                            _hover={{
                                bg: purpleHoverBg,
                                borderColor: purpleHoverBorderColor
                            }}
                        >
                            Reset Filters
                        </Button>
                    </Flex>

                    <Text fontSize="sm" fontWeight="medium" color={headingColor} mb={3}>
                        Filters
                    </Text>

                    <HStack spacing={4} mb={4}>
                        <FormControl maxW="300px">
                            <FormLabel fontSize="sm" color={textColor} fontWeight="medium">Client</FormLabel>
                            <Select
                                size="sm"
                                value={clientFilter}
                                onChange={(e) => handleClientFilterChange(e.target.value)}
                                bg={inputBg}
                                borderColor={borderColor}
                                color={headingColor}
                                _hover={{ borderColor: buttonHoverBorderColor }}
                                _focus={{
                                    borderColor: 'blue.500',
                                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                }}
                                isDisabled={filterLoading}
                            >
                                <option value="All Clients">All Clients</option>
                                {clients.map(client => (
                                    <option key={client.clientId} value={client.clientName}>
                                        {client.clientName}
                                    </option>
                                ))}
                            </Select>
                            {filterLoading && (
                                <HStack spacing={2} mt={1}>
                                    <Spinner size="xs" color="blue.500" />
                                    <Text fontSize="xs" color={textColor}>Filtering...</Text>
                                </HStack>
                            )}
                        </FormControl>

                        <FormControl maxW="300px">
                            <FormLabel fontSize="sm" color={textColor} fontWeight="medium">Application</FormLabel>
                            <Select
                                size="sm"
                                value={applicationFilter}
                                onChange={(e) => handleApplicationFilterChange(e.target.value)}
                                bg={inputBg}
                                borderColor={borderColor}
                                color={headingColor}
                                _hover={{ borderColor: buttonHoverBorderColor }}
                                _focus={{
                                    borderColor: 'blue.500',
                                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                }}
                                isDisabled={filterLoading}
                            >
                                <option value="All Applications">All Applications</option>
                                {applications.map(app => (
                                    <option key={app.appId} value={app.appName}>
                                        {app.appName}
                                    </option>
                                ))}
                            </Select>
                            {filterLoading && (
                                <HStack spacing={2} mt={1}>
                                    <Spinner size="xs" color="blue.500" />
                                    <Text fontSize="xs" color={textColor}>Filtering...</Text>
                                </HStack>
                            )}
                        </FormControl>
                        <FormControl maxW="200px">
                            <FormLabel fontSize="sm" color={textColor} fontWeight="medium">Version</FormLabel>
                            <Select
                                size="sm"
                                value={versionFilter}
                                onChange={(e) => {
                                    setVersionFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                bg={inputBg}
                                borderColor={borderColor}
                                color={headingColor}
                                _hover={{ borderColor: buttonHoverBorderColor }}
                                _focus={{
                                    borderColor: 'blue.500',
                                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                }}
                                isDisabled={filterLoading}
                            >
                                <option value="All Versions">All Versions</option>
                                {uniqueVersions.map(version => (
                                    <option key={version} value={version}>
                                        {version}
                                    </option>
                                ))}
                            </Select>
                            {filterLoading && (
                                <HStack spacing={2} mt={1}>
                                    <Spinner size="xs" color="blue.500" />
                                    <Text fontSize="xs" color={textColor}>Filtering...</Text>
                                </HStack>
                            )}
                        </FormControl>
                        <FormControl maxW="200px">
                            <FormLabel fontSize="sm" color={textColor} fontWeight="medium">Page Size</FormLabel>
                            <Select
                                size="sm"
                                value={pageSize}
                                onChange={(e) => handlePageSizeChange(e.target.value)}
                                bg={inputBg}
                                borderColor={borderColor}
                                color={headingColor}
                                _hover={{ borderColor: pageSizeHoverBorderColor }}
                                _focus={{
                                    borderColor: 'blue.500',
                                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                }}
                            >
                                <option value="20 per page">20 per page</option>
                                <option value="50 per page">50 per page</option>
                                <option value="100 per page">100 per page</option>
                            </Select>
                        </FormControl>
                    </HStack>
                </CardBody>
            </Card>

            {/* Mappings Table */}
            <Card bg={cardBg} boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
                <CardBody p={0}>
                    <Flex justify="space-between" align="center" p={4} borderBottom="1px" borderBottomColor={borderColor}>
                        <Text fontWeight="semibold" fontSize="md" color={headingColor}>
                            Client Mappings ({filteredMappings.length})
                            {clientFilter !== 'All Clients' && (
                                <Badge ml={2} colorScheme="blue" variant={badgeVariant} fontSize="xs">
                                    Filtered by: {clientFilter}
                                </Badge>
                            )}
                        </Text>
                        <HStack spacing={4} fontSize="sm" color={textColor}>
                            <Text>PAGE {currentPage} OF {totalPages || 1}</Text>
                            <Text>SHOWING {Math.min(itemsPerPage, filteredMappings.length - startIndex)} OF {filteredMappings.length}</Text>
                        </HStack>
                    </Flex>

                    {filterLoading ? (
                        <Center py={10}>
                            <VStack spacing={3}>
                                <Spinner size="lg" color="blue.500" />
                                <Text color={textColor} fontSize="md">Loading filtered results...</Text>
                            </VStack>
                        </Center>
                    ) : filteredMappings.length === 0 ? (
                        <Center py={10}>
                            <VStack spacing={3}>
                                <Text color={mutedTextColor} fontSize="lg">No mappings found</Text>
                                <Text color={mutedTextColor} fontSize="sm">
                                    {searchTerm || clientFilter !== 'All Clients'
                                        ? 'Try adjusting your filters'
                                        : 'Create your first mapping to get started'
                                    }
                                </Text>
                                {(!searchTerm && clientFilter === 'All Clients') && isAdminuser() && (
                                    <Button colorScheme="blue" size="sm" onClick={handleCreateNew}>
                                        Create Mapping
                                    </Button>
                                )}
                            </VStack>
                        </Center>
                    ) : (
                        <>
                            <TableContainer>
                                <Table variant="simple" size="sm">
                                    <Thead bg={tableHeaderBg} borderBottom="2px" borderColor={borderColor}>
                                        <Tr>
                                            <Th color={headingColor} fontWeight="semibold" fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={4}>
                                                Application
                                            </Th>
                                            <Th color={headingColor} fontWeight="semibold" fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={4}>
                                                Client
                                            </Th>
                                            <Th color={headingColor} fontWeight="semibold" fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={4}>
                                                App Type
                                            </Th>
                                            <Th color={headingColor} fontWeight="semibold" fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={4}>
                                                Mapped Version
                                            </Th>
                                            <Th color={headingColor} fontWeight="semibold" fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={4}>
                                                App Version
                                            </Th>
                                            <Th color={headingColor} fontWeight="semibold" fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={4}>
                                                Status
                                            </Th>
                                            <Th color={headingColor} fontWeight="semibold" fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={4}>
                                                Created By
                                            </Th>
                                            {isAdminuser() &&(
                                                <Th color={headingColor} fontWeight="semibold" fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={4} w="120px">
                                                Actions
                                            </Th>
                                            )}
                                            
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {paginatedMappings.map((mapping, index) => (
                                            <Tr key={`${mapping.clientId}-${mapping.appId}-${index}`} _hover={{ bg: hoverBg }}>
                                                <Td py={4} borderColor={borderColor}>
                                                    <VStack align="start" spacing={1}>
                                                        <Text fontWeight="medium" fontSize="sm" color={headingColor}>
                                                            {mapping.appName}
                                                        </Text>
                                                        <Text fontSize="xs" color={mutedTextColor}>
                                                            ID: {mapping.appId}
                                                        </Text>
                                                    </VStack>
                                                </Td>
                                                <Td py={4} borderColor={borderColor}>
                                                    <VStack align="start" spacing={1}>
                                                        <Text fontWeight="medium" fontSize="sm" color={headingColor}>
                                                            {mapping.clientName}
                                                        </Text>
                                                        <Text fontSize="xs" color={mutedTextColor}>
                                                            ID: {mapping.clientId}
                                                        </Text>
                                                    </VStack>
                                                </Td>
                                                <Td py={4} borderColor={borderColor}>
                                                    <Badge
                                                        colorScheme={getTypeBadge(mapping.appType)}
                                                        variant={badgeVariant}
                                                        fontSize="xs"
                                                        px={2}
                                                        py={1}
                                                    >
                                                        {mapping.appType}
                                                    </Badge>
                                                </Td>
                                                <Td py={4} borderColor={borderColor}>
                                                    <Badge
                                                        colorScheme="purple"
                                                        variant={badgeVariant}
                                                        fontSize="xs"
                                                        px={2}
                                                        py={1}
                                                    >
                                                        {mapping.mappingVersion || mapping.appVersion}
                                                    </Badge>
                                                </Td>
                                                <Td py={4} borderColor={borderColor}>
                                                    <Badge
                                                        colorScheme="purple"
                                                        variant={badgeVariant}
                                                        fontSize="xs"
                                                        px={2}
                                                        py={1}
                                                    >
                                                        {mapping.appVersion}
                                                    </Badge>
                                                </Td>
                                                <Td py={4} borderColor={borderColor}>
                                                    <Badge
                                                        colorScheme={getStatusBadge(mapping.isEnabled).colorScheme}
                                                        variant={badgeVariant}
                                                        fontSize="xs"
                                                        px={2}
                                                        py={1}
                                                    >
                                                        {getStatusBadge(mapping.isEnabled).label}
                                                    </Badge>
                                                </Td>
                                                <Td py={4} borderColor={borderColor}>
                                                    <Text fontSize="sm" color={textColor}>
                                                        {mapping.createdBy}
                                                    </Text>
                                                </Td>
                                                {isAdminuser() &&(
                                                    <Td py={4} borderColor={borderColor}>
                                                    <Menu>
                                                        <MenuButton
                                                            as={IconButton}
                                                            icon={<MdMoreVert />}
                                                            variant="ghost"
                                                            size="sm"
                                                            color={textColor}
                                                            _hover={{
                                                                bg: hoverBg,
                                                                color: headingColor
                                                            }}
                                                        />
                                                        <MenuList
                                                            bg={cardBg}
                                                            borderColor={borderColor}
                                                            boxShadow={menuShadow}
                                                        >
                                                            <MenuItem
                                                                icon={<MdEdit />}
                                                                onClick={() => handleEdit(mapping)}
                                                                bg={cardBg}
                                                                color={headingColor}
                                                                _hover={{ bg: hoverBg }}
                                                            >
                                                                Edit
                                                            </MenuItem>
                                                            <MenuItem
                                                                icon={<MdDelete />}
                                                                onClick={() => handleDelete(mapping)}
                                                                color="red.500"
                                                                bg={cardBg}
                                                                _hover={{ bg: redHoverBg }}
                                                            >
                                                                Delete
                                                            </MenuItem>
                                                        </MenuList>
                                                    </Menu>
                                                    </Td>
                                                )}
                                                
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TableContainer>

                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <Flex justify="space-between" align="center" p={4} borderTop="1px" borderTopColor={borderColor}>
                                    <Text fontSize="sm" color={textColor}>
                                        Showing {startIndex + 1} to {Math.min(endIndex, filteredMappings.length)} of {filteredMappings.length} entries
                                    </Text>

                                    <HStack spacing={2}>
                                        <Button
                                            size="sm"
                                            onClick={() => handlePageChange(1)}
                                            isDisabled={currentPage === 1}
                                            variant="outline"
                                            borderColor={borderColor}
                                            color={textColor}
                                            _hover={{ bg: hoverBg }}
                                            _disabled={{
                                                opacity: 0.4,
                                                cursor: 'not-allowed'
                                            }}
                                        >
                                            First
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            isDisabled={currentPage === 1}
                                            variant="outline"
                                            borderColor={borderColor}
                                            color={textColor}
                                            _hover={{ bg: hoverBg }}
                                            _disabled={{
                                                opacity: 0.4,
                                                cursor: 'not-allowed'
                                            }}
                                        >
                                            Previous
                                        </Button>

                                        {/* Page Numbers */}
                                        {(() => {
                                            const pages = [];
                                            const maxVisible = 5;
                                            let startPage = Math.max(1, currentPage - 2);
                                            let endPage = Math.min(totalPages, startPage + maxVisible - 1);

                                            if (endPage - startPage < maxVisible - 1) {
                                                startPage = Math.max(1, endPage - maxVisible + 1);
                                            }

                                            for (let i = startPage; i <= endPage; i++) {
                                                pages.push(
                                                    <Button
                                                        key={i}
                                                        size="sm"
                                                        variant={i === currentPage ? "solid" : "outline"}
                                                        colorScheme={i === currentPage ? "blue" : "gray"}
                                                        onClick={() => handlePageChange(i)}
                                                        borderColor={i === currentPage ? 'blue.500' : borderColor}
                                                        color={i === currentPage ? 'white' : textColor}
                                                        bg={i === currentPage ? 'blue.500' : 'transparent'}
                                                        _hover={{
                                                            bg: i === currentPage ? 'blue.600' : hoverBg,
                                                            borderColor: i === currentPage ? 'blue.600' : paginationHoverBorderColor
                                                        }}
                                                    >
                                                        {i}
                                                    </Button>
                                                );
                                            }
                                            return pages;
                                        })()}

                                        <Button
                                            size="sm"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            isDisabled={currentPage === totalPages}
                                            variant="outline"
                                            borderColor={borderColor}
                                            color={textColor}
                                            _hover={{ bg: hoverBg }}
                                            _disabled={{
                                                opacity: 0.4,
                                                cursor: 'not-allowed'
                                            }}
                                        >
                                            Next
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => handlePageChange(totalPages)}
                                            isDisabled={currentPage === totalPages}
                                            variant="outline"
                                            borderColor={borderColor}
                                            color={textColor}
                                            _hover={{ bg: hoverBg }}
                                            _disabled={{
                                                opacity: 0.4,
                                                cursor: 'not-allowed'
                                            }}
                                        >
                                            Last
                                        </Button>
                                    </HStack>
                                </Flex>
                            )}
                        </>
                    )}
                </CardBody>
            </Card>

            {/* Create Mapping Modal - Professional Design */}
            <ClientAppMappingModal
                isOpen={isOpen}
                onClose={handleModalClose}
                mappingData={mappingData}
                handleClientChange={handleClientChange}
                handleApplicationSelection={handleApplicationSelection}
                handleBulkApplicationSelection={handleBulkApplicationSelection}
                handleVersionChange={handleVersionChange}
                clients={clients}
                availableApplications={availableApplications}
                mappingLoading={mappingLoading}
                handleCreateMapping={handleCreateMapping}
                handleCreateBulkMapping={handleCreateBulkMapping}
                mappingMode={mappingMode}
                setMappingMode={setMappingMode}
                filteredApplications={filteredApplicationsModal}
                mappingSearchTerm={mappingSearchTerm}
                setmappingSearchTerm={setmappingSearchTerm}
            />
            <Modal isOpen={isEditOpen} onClose={handleEditModalClose} size="lg" isCentered>
                <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(6px)" />
                <ModalContent
                    bg={modalBg}
                    borderRadius="xl"
                    boxShadow="2xl"
                    border="2px solid"
                    borderColor={modalBorderColor}
                    mx={4}
                >
                    <ModalHeader
                        bg={modalHeaderGradient}
                        color={modalHeaderText}
                        borderTopRadius="xl"
                        py={6}
                        px={8}
                    >
                        <HStack spacing={3}>
                            <Box
                                p={2}
                                bg={modalHeaderIconBg}
                                borderRadius="lg"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <MdEdit size={20} />
                            </Box>
                            <VStack align="start" spacing={0}>
                                <Text fontSize="xl" fontWeight="bold" lineHeight="1.2">
                                    Edit Application Mapping
                                </Text>
                                <Text fontSize="sm" opacity={0.9} fontWeight="normal">
                                    Modify mapping configuration and settings
                                </Text>
                            </VStack>
                        </HStack>
                    </ModalHeader>

                    <ModalCloseButton
                        color={modalHeaderText}
                        size="lg"
                        top={6}
                        right={6}
                        _hover={{ bg: modalCloseButtonHover }}
                    />

                    <ModalBody p={8}>
                        {editingMapping && (
                            <VStack spacing={6} align="stretch">
                                {/* Mapping Overview Alert */}
                                <Alert
                                    status="info"
                                    bg={alertInfoBg}
                                    borderColor={alertInfoBorderColor}
                                    borderWidth="2px"
                                    borderRadius="xl"
                                    p={6}
                                >
                                    <AlertIcon color={alertInfoIconColor} />
                                    <Box>
                                        <AlertTitle fontSize="lg" color={alertInfoTitleColor} fontWeight="bold" mb={2}>
                                            Editing Mapping Configuration
                                        </AlertTitle>
                                        <AlertDescription fontSize="sm" color={alertInfoDescColor} lineHeight="1.5">
                                            You are modifying the mapping between <Text as="span" fontWeight="semibold">{editingMapping.appName}</Text> and <Text as="span" fontWeight="semibold">{editingMapping.clientName}</Text>. Changes will take effect immediately after saving.
                                        </AlertDescription>
                                    </Box>
                                </Alert>

                                {/* Mapping Configuration Section */}
                                <Box
                                    bg={summarySectionBg}
                                    p={6}
                                    borderWidth="2px"
                                    borderColor={summarySectionBorderColor}
                                    borderRadius="xl"
                                    position="relative"
                                    overflow="hidden"
                                >
                                    {/* Background pattern */}
                                    <Box
                                        position="absolute"
                                        top={-2}
                                        right={-2}
                                        w={20}
                                        h={20}
                                        bg={appPreviewAccentColor}
                                        borderRadius="full"
                                        opacity={0.05}
                                    />

                                    <VStack spacing={6} align="stretch" position="relative">
                                        <Text fontSize="md" fontWeight="bold" color={formLabelColor} mb={2}>
                                            Mapping Configuration
                                        </Text>

                                        {/* Status Toggle */}
                                        <HStack
                                            justify="space-between"
                                            align="center"
                                            p={4}
                                            bg={formInputBg}
                                            borderRadius="lg"
                                            borderWidth="1px"
                                            borderColor={formInputBorderColor}
                                        >
                                            <VStack align="start" spacing={1}>
                                                <Text fontSize="sm" fontWeight="semibold" color={formLabelColor}>
                                                    Mapping Status
                                                </Text>
                                                <Text fontSize="xs" color={loadingTextColor}>
                                                    Enable or disable this mapping
                                                </Text>
                                            </VStack>
                                            <HStack spacing={3}>
                                                <Switch
                                                    isChecked={editingMapping.isEnabled !== false}
                                                    onChange={(e) => {
                                                        setEditingMapping(prev => ({
                                                            ...prev,
                                                            isEnabled: e.target.checked
                                                        }));
                                                    }}
                                                    colorScheme="green"
                                                    size="lg"
                                                />
                                                <Badge
                                                    variant="solid"
                                                    fontSize="sm"
                                                    px={3}
                                                    py={1}
                                                    borderRadius="full"
                                                    fontWeight="semibold"
                                                    bg={editingMapping.isEnabled !== false ? badgeGreenBg : badgeRedBg}
                                                    color={badgeTextColor}
                                                >
                                                    {editingMapping.isEnabled !== false ? "ACTIVE" : "INACTIVE"}
                                                </Badge>
                                            </HStack>
                                        </HStack>

                                        {/* Version Input */}
                                        <VStack spacing={4} align="stretch">
                                            {/* Version Input Method Selector */}
                                            <Box
                                                bg={versionGuidelinesBg}
                                                p={4}
                                                borderRadius="lg"
                                                borderWidth="1px"
                                                borderColor={versionGuidelinesBorderColor}
                                            >
                                                <Text fontSize="sm" color={versionGuidelinesTitleColor} fontWeight="medium" mb={3}>
                                                    Choose Version Input Method:
                                                </Text>
                                                <RadioGroup
                                                    value={editVersionInputMethod}
                                                    onChange={handleEditVersionInputMethodChange}
                                                >
                                                    <Stack spacing={3}>
                                                        <Radio value="select" colorScheme="blue" size="md">
                                                            <VStack align="start" spacing={1} ml={2}>
                                                                <Text fontSize="sm" fontWeight="medium" color={formLabelColor}>
                                                                    Select from available versions
                                                                </Text>
                                                                <Text fontSize="xs" color={versionGuidelinesTextColor}>
                                                                    Choose from pre-defined versions between {editingMapping?.minimalVersion} and {editingMapping?.appVersion}
                                                                </Text>
                                                            </VStack>
                                                        </Radio>
                                                        <Radio value="manual" colorScheme="blue" size="md">
                                                            <VStack align="start" spacing={1} ml={2}>
                                                                <Text fontSize="sm" fontWeight="medium" color={formLabelColor}>
                                                                    Enter version manually
                                                                </Text>
                                                                <Text fontSize="xs" color={versionGuidelinesTextColor}>
                                                                    Input a custom version within the supported range
                                                                </Text>
                                                            </VStack>
                                                        </Radio>
                                                    </Stack>
                                                </RadioGroup>
                                            </Box>

                                            <Box>
                                                <FormLabel
                                                    color={formLabelColor}
                                                    fontSize="sm"
                                                    fontWeight="semibold"
                                                    mb={2}
                                                >
                                                    Mapping Version
                                                </FormLabel>
                                                <Text fontSize="xs" color={loadingTextColor} mb={3}>
                                                    Current application version: <Text as="span" fontWeight="semibold" color={versionPreviewTextColor}>{editingMapping?.appVersion}</Text>
                                                    {editingMapping?.minimalVersion && (
                                                        <> • Minimal version: <Text as="span" fontWeight="semibold" color="orange.500">{editingMapping.minimalVersion}</Text></>
                                                    )}
                                                </Text>

                                                {editVersionInputMethod === 'select' ? (
                                                    <Select
                                                        value={editingMapping?.mappingVersion ?? editingMapping?.appVersion}
                                                        onChange={(e) => {
                                                            setEditingMapping(prev => ({
                                                                ...prev,
                                                                mappingVersion: e.target.value
                                                            }));
                                                            setEditVersionValidationError('');
                                                        }}
                                                        bg={formInputBg}
                                                        borderColor={formInputBorderColor}
                                                        color={formInputTextColor}
                                                        size="lg"
                                                        borderRadius="lg"
                                                        _hover={{
                                                            borderColor: formInputHoverBorderColor,
                                                            boxShadow: `0 0 0 1px ${formInputHoverBorderColor}`
                                                        }}
                                                        _focus={{
                                                            borderColor: formInputFocusBorderColor,
                                                            boxShadow: `0 0 0 3px ${formInputFocusShadowColor}`
                                                        }}
                                                    >
                                                        {editingMapping && generateVersionOptions(editingMapping.appVersion, editingMapping.minimalVersion).map((version, index, arr) => (
                                                            <option key={version} value={version}>
                                                                {version}
                                                                {index === 0 && ' (Latest)'}
                                                                {index === arr.length - 1 && arr.length > 1 && ' (Minimum)'}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                ) : (
                                                    <Input
                                                        value={editingMapping?.mappingVersion ?? editingMapping?.appVersion}
                                                        onChange={(e) => handleManualEditVersionChange(e.target.value)}
                                                        placeholder={`Enter version between ${editingMapping?.minimalVersion} and ${editingMapping?.appVersion}...`}
                                                        bg={formInputBg}
                                                        borderColor={editVersionValidationError ? 'red.300' : formInputBorderColor}
                                                        color={formInputTextColor}
                                                        size="lg"
                                                        borderRadius="lg"
                                                        _hover={{
                                                            borderColor: editVersionValidationError ? 'red.300' : formInputHoverBorderColor,
                                                            boxShadow: editVersionValidationError ? '0 0 0 1px red' : `0 0 0 1px ${formInputHoverBorderColor}`
                                                        }}
                                                        _focus={{
                                                            borderColor: editVersionValidationError ? 'red.500' : formInputFocusBorderColor,
                                                            boxShadow: editVersionValidationError ? '0 0 0 3px rgba(255, 0, 0, 0.2)' : `0 0 0 3px ${formInputFocusShadowColor}`
                                                        }}
                                                        maxLength={20}
                                                    />
                                                )}

                                                {/* Error Message */}
                                                {editVersionValidationError && (
                                                    <Text fontSize="sm" color="red.500" mt={2}>
                                                        {editVersionValidationError}
                                                    </Text>
                                                )}
                                            </Box>

                                            {/* Version Guidelines */}
                                            <Box
                                                bg={versionGuidelinesBg}
                                                p={4}
                                                borderRadius="lg"
                                                borderWidth="1px"
                                                borderColor={versionGuidelinesBorderColor}
                                            >
                                                <Text fontSize="sm" color={versionGuidelinesTitleColor} fontWeight="medium" mb={2}>
                                                    Version Guidelines:
                                                </Text>
                                                <VStack spacing={1} align="start">
                                                    <Text fontSize="xs" color={versionGuidelinesTextColor}>
                                                        • Latest version ({editingMapping?.appVersion}) recommended for optimal compatibility
                                                    </Text>
                                                    <Text fontSize="xs" color={versionGuidelinesTextColor}>
                                                        • Minimum supported version: {editingMapping?.minimalVersion}
                                                    </Text>
                                                    <Text fontSize="xs" color={versionGuidelinesTextColor}>
                                                        • {editVersionInputMethod === 'manual' ? 'Use format like 1.0, 2.1.3, etc.' : 'Choose appropriate version based on client requirements'}
                                                    </Text>
                                                    {editVersionInputMethod === 'manual' && (
                                                        <Text fontSize="xs" color={versionGuidelinesTextColor}>
                                                            • Version will be validated to ensure it's within the supported range
                                                        </Text>
                                                    )}
                                                </VStack>
                                            </Box>

                                            {/* Version Preview */}
                                            {editingMapping?.mappingVersion && !editVersionValidationError && (
                                                <Box
                                                    bg={versionPreviewBg}
                                                    borderWidth="2px"
                                                    borderColor={versionPreviewBorderColor}
                                                    borderRadius="lg"
                                                    p={4}
                                                >
                                                    <HStack spacing={3} justify="space-between">
                                                        <HStack spacing={3}>
                                                            <Badge
                                                                variant="solid"
                                                                fontSize="sm"
                                                                px={3}
                                                                py={1}
                                                                borderRadius="md"
                                                                bg={versionBadgeBg}
                                                                color={versionBadgeTextColor}
                                                            >
                                                                {editingMapping.mappingVersion}
                                                            </Badge>
                                                            <Text fontSize="sm" color={versionPreviewTextColor} fontWeight="medium">
                                                                Updated version ready
                                                            </Text>
                                                        </HStack>
                                                        <HStack spacing={2}>
                                                            {editingMapping.mappingVersion === editingMapping.appVersion && (
                                                                <Badge variant="solid" colorScheme="green" fontSize="xs">
                                                                    Latest
                                                                </Badge>
                                                            )}
                                                            {editingMapping.mappingVersion === editingMapping.minimalVersion && (
                                                                <Badge variant="solid" colorScheme="orange" fontSize="xs">
                                                                    Minimum
                                                                </Badge>
                                                            )}
                                                            {editVersionInputMethod === 'manual' &&
                                                                editingMapping.mappingVersion !== editingMapping.appVersion &&
                                                                editingMapping.mappingVersion !== editingMapping.minimalVersion && (
                                                                    <Badge variant="solid" colorScheme="purple" fontSize="xs">
                                                                        Custom
                                                                    </Badge>
                                                                )}
                                                        </HStack>
                                                    </HStack>
                                                </Box>
                                            )}
                                        </VStack>
                                    </VStack>
                                </Box>

                                {/* Collapsible Mapping Details Dropdown */}
                                <Box>
                                    {/* Dropdown Header */}
                                    <Box
                                        as="button"
                                        w="full"
                                        onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
                                        bg={summarySectionBg}
                                        p={5}
                                        borderWidth="2px"
                                        borderColor={summarySectionBorderColor}
                                        borderRadius="xl"
                                        _hover={{
                                            bg: versionGuidelinesBg,
                                            borderColor: appPreviewBorderColor,
                                            transform: "translateY(-1px)",
                                            boxShadow: "md"
                                        }}
                                        transition="all 0.3s ease"
                                        cursor="pointer"
                                        position="relative"
                                        overflow="hidden"
                                    >
                                        {/* Background accent */}
                                        <Box
                                            position="absolute"
                                            top={0}
                                            left={0}
                                            right={0}
                                            bottom={0}
                                            bg={appPreviewAccentColor}
                                            opacity={isDetailsExpanded ? 0.08 : 0.03}
                                            transition="opacity 0.3s ease"
                                        />

                                        <HStack justify="space-between" align="center" position="relative">
                                            <VStack align="start" spacing={1}>
                                                <HStack spacing={2}>
                                                    <Text fontSize="md" fontWeight="bold" color={formLabelColor} textAlign="left">
                                                        Mapping Details
                                                    </Text>
                                                    <Badge
                                                        variant="subtle"
                                                        fontSize="xs"
                                                        px={2}
                                                        py={1}
                                                        borderRadius="full"
                                                        bg={isDetailsExpanded ? versionBadgeBg : summarySectionBorderColor}
                                                        color={isDetailsExpanded ? versionBadgeTextColor : summaryTextColor}
                                                        transition="all 0.3s ease"
                                                    >
                                                        {isDetailsExpanded ? 'Expanded' : 'Collapsed'}
                                                    </Badge>
                                                </HStack>
                                                <Text fontSize="xs" color={loadingTextColor} textAlign="left">
                                                    {isDetailsExpanded ? 'Click to hide' : 'Click to view'} application and client information
                                                </Text>
                                            </VStack>
                                            <Box
                                                transform={isDetailsExpanded ? "rotate(180deg)" : "rotate(0deg)"}
                                                transition="transform 0.3s ease"
                                            >
                                                <IconButton
                                                    icon={<MdExpandMore />}
                                                    variant="ghost"
                                                    size="lg"
                                                    color={formLabelColor}
                                                    _hover={{ bg: 'transparent' }}
                                                    pointerEvents="none"
                                                    fontSize="24px"
                                                />
                                            </Box>
                                        </HStack>
                                    </Box>

                                    {/* Collapsible Content with Animation */}
                                    <Collapse in={isDetailsExpanded} animateOpacity startingHeight={0}>
                                        <Box
                                            mt={4}
                                            opacity={isDetailsExpanded ? 1 : 0}
                                            transform={isDetailsExpanded ? "translateY(0)" : "translateY(-10px)"}
                                            transition="all 0.4s ease"
                                        >
                                            <VStack spacing={4} align="stretch">
                                                <HStack spacing={4} align="stretch">
                                                    {/* Application Details */}
                                                    <Box
                                                        flex={1}
                                                        bg={appPreviewBg}
                                                        p={5}
                                                        borderWidth="2px"
                                                        borderColor={appPreviewBorderColor}
                                                        borderRadius="xl"
                                                        transform="translateY(0)"
                                                        opacity={1}
                                                        transition="all 0.5s ease 0.1s"
                                                    >
                                                        <VStack align="start" spacing={3}>
                                                            <HStack spacing={2} mb={2}>
                                                                <Text fontSize="sm" fontWeight="bold" color={appPreviewTitleColor}>
                                                                    Application
                                                                </Text>
                                                                <Badge
                                                                    variant="solid"
                                                                    fontSize="xs"
                                                                    px={2}
                                                                    py={1}
                                                                    borderRadius="full"
                                                                    bg={badgePurpleBg}
                                                                    color={badgeTextColor}
                                                                >
                                                                    {editingMapping.appType}
                                                                </Badge>
                                                            </HStack>

                                                            <VStack align="start" spacing={2} w="full">
                                                                <Box>
                                                                    <Text fontSize="xs" color={appPreviewSubtitleColor} mb={1}>Name</Text>
                                                                    <Text fontSize="sm" fontWeight="semibold" color={appPreviewTitleColor}>
                                                                        {editingMapping.appName}
                                                                    </Text>
                                                                </Box>
                                                                <Box>
                                                                    <Text fontSize="xs" color={appPreviewSubtitleColor} mb={1}>ID</Text>
                                                                    <Text fontSize="xs" color={appPreviewDescColor} fontFamily="mono">
                                                                        {editingMapping.appId}
                                                                    </Text>
                                                                </Box>
                                                            </VStack>
                                                        </VStack>
                                                    </Box>

                                                    {/* Client Details */}
                                                    <Box
                                                        flex={1}
                                                        bg={summarySectionBg}
                                                        p={5}
                                                        borderWidth="2px"
                                                        borderColor={summarySectionBorderColor}
                                                        borderRadius="xl"
                                                        transform="translateY(0)"
                                                        opacity={1}
                                                        transition="all 0.5s ease 0.2s"
                                                    >
                                                        <VStack align="start" spacing={3}>
                                                            <Text fontSize="sm" fontWeight="bold" color={formLabelColor} mb={2}>
                                                                Client
                                                            </Text>

                                                            <VStack align="start" spacing={2} w="full">
                                                                <Box>
                                                                    <Text fontSize="xs" color={loadingTextColor} mb={1}>Name</Text>
                                                                    <Text fontSize="sm" fontWeight="semibold" color={formLabelColor}>
                                                                        {editingMapping.clientName}
                                                                    </Text>
                                                                </Box>
                                                                <Box>
                                                                    <Text fontSize="xs" color={loadingTextColor} mb={1}>ID</Text>
                                                                    <Text fontSize="xs" color={summaryTextColor} fontFamily="mono">
                                                                        {editingMapping.clientId}
                                                                    </Text>
                                                                </Box>
                                                            </VStack>
                                                        </VStack>
                                                    </Box>
                                                </HStack>

                                                {/* Mapping Metadata */}
                                                <Box
                                                    bg={versionGuidelinesBg}
                                                    p={5}
                                                    borderWidth="1px"
                                                    borderColor={versionGuidelinesBorderColor}
                                                    borderRadius="xl"
                                                    transform="translateY(0)"
                                                    opacity={1}
                                                    transition="all 0.5s ease 0.3s"
                                                >
                                                    <VStack align="start" spacing={3}>
                                                        <Text fontSize="sm" fontWeight="bold" color={versionGuidelinesTitleColor}>
                                                            Mapping Information
                                                        </Text>

                                                        <HStack spacing={6} w="full">
                                                            <Box>
                                                                <Text fontSize="xs" color={versionGuidelinesTextColor} mb={1}>Created By</Text>
                                                                <Text fontSize="sm" fontWeight="medium" color={versionGuidelinesTitleColor}>
                                                                    {editingMapping.createdBy}
                                                                </Text>
                                                            </Box>
                                                            {editingMapping.createdAt && (
                                                                <Box>
                                                                    <Text fontSize="xs" color={versionGuidelinesTextColor} mb={1}>Created At</Text>
                                                                    <Text fontSize="sm" color={versionGuidelinesTitleColor}>
                                                                        {new Date(editingMapping.createdAt).toLocaleDateString()}
                                                                    </Text>
                                                                </Box>
                                                            )}
                                                        </HStack>
                                                    </VStack>
                                                </Box>
                                            </VStack>
                                        </Box>
                                    </Collapse>
                                </Box>
                            </VStack>
                        )}
                    </ModalBody>

                    <ModalFooter bg={modalFooterBg} borderBottomRadius="xl" p={8}>
                        <HStack spacing={4} w="full" justify="end">
                            <Button
                                variant="ghost"
                                onClick={handleEditModalClose}
                                color={cancelButtonTextColor}
                                size="lg"
                                isDisabled={mappingLoading}
                                _hover={{ bg: cancelButtonHoverBg }}
                                px={8}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleUpdateMapping}
                                isLoading={mappingLoading}
                                loadingText="Updating..."
                                size="lg"
                                px={8}
                                fontWeight="semibold"
                                bg={primaryButtonBg}
                                color={primaryButtonTextColor}
                                _hover={{
                                    bg: primaryButtonHoverBg,
                                    transform: "translateY(-1px)",
                                    boxShadow: "lg"
                                }}
                                _disabled={{
                                    bg: primaryButtonDisabledBg,
                                    color: primaryButtonDisabledTextColor
                                }}
                                transition="all 0.2s"
                                isDisabled={mappingLoading || !!editVersionValidationError}
                            >
                                Update Mapping
                            </Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default ClientMappingsManagement;